import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Home, Car, Briefcase, Heart, HeartPulse, Umbrella, Scale, Clock, ArrowRight, CheckCircle, ExternalLink, Plane, MapPin } from "lucide-react";

const products = [
  {
    icon: Home,
    title: "Home Insurance",
    description: "Protect your biggest investment with comprehensive homeowners coverage. Our policies cover home damage, personal belongings, and liability.",
    features: ["Dwelling protection", "Personal property coverage", "Liability protection", "Additional living expenses"],
    color: "bg-blue-50",
    area: "IN, IL, MI, OH, FL",
  },
  {
    icon: Car,
    title: "Auto Insurance",
    description: "Get tailored auto insurance that meets your state requirements and protects your vehicle's value. Safe driver discounts available.",
    features: ["Liability coverage", "Collision & comprehensive", "Uninsured motorist protection", "Multi-vehicle discounts"],
    color: "bg-amber-50",
    area: "IN, IL, MI, OH, FL",
  },
  {
    icon: Briefcase,
    title: "Commercial Insurance",
    description: "Safeguard your business with customized commercial insurance solutions including general liability, property, and workers' compensation.",
    features: ["General liability", "Commercial property", "Professional liability", "Workers' compensation"],
    color: "bg-emerald-50",
    area: "IN, IL, MI, OH, FL",
  },
  {
    icon: Heart,
    title: "Life Insurance",
    description: "Secure your family's financial future with reliable life insurance. We offer both term and whole life policies to fit your budget.",
    features: ["Term life insurance", "Whole life insurance", "Universal life", "Final expense coverage"],
    color: "bg-rose-50",
    area: "IN, IL, MI, OH, FL",
  },
  {
    icon: HeartPulse,
    title: "Health Insurance",
    description: "Access quality healthcare with comprehensive health insurance plans that cover medical and surgical expenses for you and your family.",
    features: ["Individual plans", "Family coverage", "Supplemental health", "Medicare supplement"],
    color: "bg-purple-50",
    area: "IN, IL, MI, OH, FL",
  },
  {
    icon: Umbrella,
    title: "Annuities",
    description: "Build a stable retirement with our annuity investment options. Enjoy guaranteed income streams and tax-deferred growth.",
    features: ["Fixed annuities", "Variable annuities", "Indexed annuities", "Retirement planning"],
    color: "bg-teal-50",
    area: "IN, IL, MI, OH, FL",
  },
  {
    icon: Clock,
    title: "Long-term Care",
    description: "Plan ahead with long-term care insurance. Cover nursing home care, assisted living, and home health services for peace of mind.",
    features: ["Nursing home coverage", "Assisted living", "Home health care", "Adult day care"],
    color: "bg-orange-50",
    area: "IN, IL, MI, OH, FL",
  },
  {
    icon: Plane,
    title: "Travel Insurance",
    description: "Travel with confidence knowing you're protected. Coverage for trip cancellation, medical emergencies, baggage loss, and more.",
    features: ["Trip cancellation protection", "Emergency medical coverage", "Baggage loss & delay", "Travel accident insurance"],
    color: "bg-sky-50",
    area: "Available anywhere in the USA",
  },
];

export default function ProductsPage() {
  return (
    <div>
      {/* Hero */}
      <section className="hero-gradient py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4 animate-fade-up">Our Products</h1>
          <p className="text-white/70 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto animate-fade-up delay-100">
            Comprehensive insurance solutions serving Northwest Indiana, Chicagoland, and clients in Indiana, Illinois, Michigan, Ohio & Florida.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section data-testid="products-grid" className="bg-[#F8FAFC] py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {products.map((product, i) => (
              <Card
                key={i}
                data-testid={`product-card-${product.title.toLowerCase().replace(/\s/g, '-')}`}
                className="bg-white rounded-xl border border-slate-100 overflow-hidden card-hover"
              >
                <CardContent className="p-0">
                  <div className="flex flex-col lg:flex-row">
                    <div className="lg:w-2/3 p-8 lg:p-10">
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`w-12 h-12 ${product.color} rounded-lg flex items-center justify-center`}>
                          <product.icon className="h-6 w-6 text-[#002147]" />
                        </div>
                        <h3 className="font-heading text-2xl font-bold text-[#002147]">{product.title}</h3>
                      </div>
                      <p className="text-slate-500 leading-relaxed mb-6">{product.description}</p>
                      <Link to="/contact">
                        <Button data-testid={`quote-btn-${product.title.toLowerCase().replace(/\s/g, '-')}`} className="bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold rounded-md">
                          Get a Quote
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </Link>
                      {product.area && (
                        <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
                          <MapPin className="h-3 w-3" />
                          {product.area}
                        </p>
                      )}
                    </div>
                    <div className="lg:w-1/3 bg-[#F8FAFC] p-8 lg:p-10 border-t lg:border-t-0 lg:border-l border-slate-100">
                      <h4 className="font-semibold text-[#002147] text-sm uppercase tracking-wide mb-4">Coverage Includes</h4>
                      <ul className="space-y-3">
                        {product.features.map((f, fi) => (
                          <li key={fi} className="flex items-center gap-3 text-sm text-slate-600">
                            <CheckCircle className="h-4 w-4 text-[#D4AF37] shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* LegalShield Section */}
      <section data-testid="legalshield-section" className="bg-white py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#002147] rounded-2xl p-8 lg:p-12 flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="h-8 w-8 text-[#D4AF37]" />
                <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wide">Partner Product</span>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">LegalShield</h2>
              <p className="text-white/70 leading-relaxed mb-4">
                Smart, simple coverage starts here. LegalShield provides affordable legal protection for you, your family, and your small business. Plans start at just $29.95/month. Available in any state that doesn't require insurance licensing.
              </p>
              <ul className="space-y-2 mb-6">
                {["24/7 Emergency Legal Access", "Document & Contract Review", "Identity Theft Protection (IDShield)", "Small Business Legal Plans", "Commercial Drivers Legal Plans"].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-white/60 text-sm">
                    <CheckCircle className="h-4 w-4 text-[#D4AF37] shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="https://johndanderson.legalshieldassociate.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  data-testid="legalshield-cta-btn"
                  className="bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold px-8 py-3 h-auto rounded-md"
                >
                  Explore LegalShield Plans
                  <ExternalLink className="h-4 w-4 ml-2" />
                </Button>
              </a>
            </div>
            <div className="lg:w-1/3">
              <div className="bg-white/5 rounded-xl p-8 text-center border border-white/10">
                <Scale className="h-16 w-16 text-[#D4AF37] mx-auto mb-4" />
                <p className="text-white font-heading text-xl font-bold mb-2">Legal Plans</p>
                <p className="text-white/50 text-sm mb-4">Starting at</p>
                <p className="text-[#D4AF37] font-heading text-4xl font-bold">$29.95<span className="text-lg text-white/50">/mo</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hero-gradient py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
            Not Sure Which Coverage You Need?
          </h2>
          <p className="text-white/70 text-base lg:text-lg mb-8 max-w-xl mx-auto">
            Our team will help you understand your options and find the perfect insurance solution.
          </p>
          <Link to="/contact">
            <Button data-testid="products-cta-btn" className="bg-[#D4AF37] text-[#002147] hover:bg-[#B5952F] font-bold px-8 py-3 h-auto text-base rounded-md shadow-lg">
              Talk to an Expert
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
